<script>
import Spinner from '@/components/general/spinner.vue'
import { mapState, mapActions } from 'vuex'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import dayjs from '@/services/date'
import DateFormatToken from '@/constants/date/DateFormatToken'

export default {
  name: 'InvoiceCenterBulkActionDialogContent',
  components: {
    Spinner,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data: () => {
    return {
      loadingBookingIds: [],
      actionProcessed: [],
      keys: ['pendingCount', 'candidateCount', 'clientCount'],
    }
  },

  computed: {
    ...mapState('disputes', {
      invoiceCenter: (state) => state.flyout.invoiceCenter,
      invoiceCenterDateUntil: (state) => state.flyout.invoiceCenterDateUntil,
    }),
    compDateUntil() {
      return dayjs(this.invoiceCenterDateUntil).format(
        DateFormatToken.ddddDDMMMMYYYY
      )
    },

    bulkActions() {
      return this.keys
        .filter((key) => key !== 'invoiceCenterId')
        .map((key) => ({
          // mapping button data by count key
          count: this.invoiceCenter[key],
          text:
            key === 'pendingCount'
              ? 'with no additional timesheets'
              : key === 'candidateCount'
              ? 'where candidate has submitted with no change'
              : 'where client has submitted with no change',
          icon:
            key === 'pendingCount'
              ? 'mdi-clipboard-check'
              : key === 'candidateCount'
              ? 'mdi-account'
              : 'mdi-home-variant',
          operation:
            key === 'pendingCount'
              ? ResolveOperations.ORIGINAL
              : key === 'candidateCount'
              ? ResolveOperations.CANDIDATE
              : ResolveOperations.CLIENT,
        }))
    },
  },
  methods: {
    ...mapActions({
      resetInvoiceCenterCounterAfterInsert:
        'disputes/resetInvoiceCenterCounterAfterInsert',
    }),
    async submitBulkAction(operation) {
      this.loadingBookingIds.push(operation)

      const result = await this.$store.dispatch(
        'disputes/bulkResolveByInvoiceCenter',
        {
          operation,
          id: this.invoiceCenter.id,
          dateUntil: this.invoiceCenterDateUntil,
        }
      )

      this.loadingBookingIds = this.loadingBookingIds.filter(
        (loadingOp) => loadingOp !== operation
      )

      if (!result.isSuccess) return

      this.actionProcessed.push(operation)

      this.resetInvoiceCenterCounterAfterInsert({
        invoiceCenterId: this.invoiceCenter.id,
        operation,
      })
    },
  },
}
</script>

<template>
  <div v-if="!loading">
    <h1>{{ invoiceCenter.name }}</h1>
    <h4>Showing bookings up to {{ compDateUntil }}</h4>
    <div class="d-flex">
      <div
        v-for="({ count, text, icon, operation }, index) in bulkActions"
        :key="index"
        class="bulk-action-card p-relative mt-10 mx-2 w-full border rounded pa-6"
      >
        <transition name="fade-in">
          <div
            v-if="actionProcessed.includes(operation)"
            class="saved-overlay rounded d-flex justify-center align-center"
          >
            <v-icon color="white" size="100">mdi-check-circle-outline</v-icon>
          </div>
        </transition>

        <div
          v-if="!loadingBookingIds.includes(operation)"
          class="d-flex flex-column justify-space-between h-full"
        >
          <v-icon class="mb-4" size="60">{{ icon }}</v-icon>
          <p class="font-weight-medium text-center text-body-1 px-6"
            >Submit
            <span class="warning--text font-weight-bold">{{ count }}</span>
            Bookings {{ text }} as
            <span class="success--text">Ready to Invoice</span>
          </p>
          <v-btn
            color="primary"
            class="py-6 px-8"
            depressed
            :disabled="actionProcessed.includes(operation)"
            @click="submitBulkAction(operation)"
            >Submit</v-btn
          >
        </div>
        <Spinner v-else size="60" class="mt-10" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bulk-action-card {
  position: relative;
  width: 33%;
  height: 300px;
}

.saved-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: rgba(81, 197, 85, 0.9);
}

.h-full {
  height: 100%;
}

.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.3s;
}
.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}
</style>
